import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    CircularProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const RAILWAY_API = 'https://soldikeeper-backend-production.up.railway.app';

const VerifyEmail = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
    const [message, setMessage] = useState('');

    useEffect(() => {
        const verify = async () => {
            try {
                const res = await fetch(`${RAILWAY_API}/api/auth/verify-email/${token}`);
                const data = await res.json();

                if (data.success && data.data?.token) {
                    // Store JWT and redirect to dashboard
                    localStorage.setItem('token', data.data.token);
                    setStatus('success');
                    setMessage(data.message || 'Email verified! Redirecting to your dashboard…');
                    setTimeout(() => navigate('/dashboard'), 3000);
                } else {
                    setStatus('error');
                    setMessage(data.message || 'The verification link is invalid or has expired.');
                }
            } catch {
                setStatus('error');
                setMessage('Network error. Please try again or request a new verification link.');
            }
        };

        if (token) {
            verify();
        } else {
            setStatus('error');
            setMessage('No verification token provided.');
        }
    }, [token, navigate]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 2
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    maxWidth: 440,
                    width: '100%',
                    textAlign: 'center',
                    borderRadius: 2
                }}
            >
                {status === 'loading' && (
                    <>
                        <CircularProgress size={64} sx={{ mb: 2 }} />
                        <Typography variant="h6">Verifying your email…</Typography>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircleOutlineIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Email Verified!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {message}
                        </Typography>
                        <Button variant="contained" onClick={() => navigate('/dashboard')}>
                            Go to Dashboard
                        </Button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <ErrorOutlineIcon sx={{ fontSize: 72, color: 'error.main', mb: 2 }} />
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Verification Failed
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {message}
                        </Typography>
                        <Button
                            variant="outlined"
                            sx={{ mr: 1 }}
                            onClick={() => navigate('/email-pending', { state: {} })}
                        >
                            Resend Link
                        </Button>
                        <Button variant="contained" onClick={() => navigate('/login')}>
                            Back to Login
                        </Button>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default VerifyEmail;
